import { useEffect, useState } from 'react';
import api from '../../api/axios';
import StatusBadge from '../../components/StatusBadge';
import StatusFilter from '../../components/StatusFilter';
import RequestDetail from '../../components/RequestDetail';
import RequestComments from '../../components/RequestComments';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import RequestFormModal from '../../components/RequestFormModal';

const CODE_FIELD = {
  material_requests: { column: 'material_number', label: 'Material Code' },
  vendor_requests:   { column: 'business_partner_number', label: 'Vendor Code' },
};

const PROCESSABLE = ['approved', 'in_progress'];

const DECISION_STYLE = {
  approved: { dot: '#4f6ef7', bg: '#eef2ff', label: 'Approved' },
  rejected: { dot: '#dc2626', bg: '#fee2e2', label: 'Rejected' },
  pending:  { dot: '#f59e0b', bg: '#fef3c7', label: 'Waiting' },
};

// ── SAP Excel export ─────────────────────────────────────────────────────────
const SAP_HEADERS = [
  'Excel Line number','Material Number','Industry Sector','Material Type','Plant','Storage Location',
  'Valuation Type','Sales Organization','Material Stats Group','Distribution Channel',
  'Basic Data 1','Basic Data 2','Classification','Sales view 1','Sales View 2','Sales/Plant data',
  'View1','Purchasing','Storage View 1','Storage View 2','Accounting View 1','Accounting View 2',
  'MRP View 1','MRP View 2','MRP View 3','MRP View 4','Quality View','Costing View 1','Costing View 2',
  'Material Description','Base UOM','Material Group','Old Material Number','Division',
  'Cross-Plant Material Status','Date from which the cross-plant','Gross Weight','Net Weight',
  'Weight Unit','Unit of weight in ISO code','Volume','Volume Unit','Size/Dimensions',
  'Packaging Material Group','Basic Material','Industry Desc','Class Type','Class','Sales Unit',
  'Denominator','Numerator','Cross-distribution-chain mat status','Dist Chain Mat Status',
  'Status Valid From','Tax Classification','Delivering Plant','Gen Item Category Group',
  'Item Category Group','Mat Pricing Group','Acct Assignment Group','Availability Check',
  'Transport Group','Loading Group','Packaging Material Type','Allowed packaging weight',
  'Unit of weight (allowed packaging weight)','Allowed packaging volume',
  'Volume unit (allowed packaging volume)','Material Group 1','Material Group 2','Material Group 3',
  'Material Group 4','Material Group 5','Material statistics group','Commodity Code',
  'Export/Import Group','Control Code','Country of Origin','Region of Origin',
  'Purchasing Value Key','Purchasing Group','Batch Mgmt','GR Proc Time','Quota arrangement usage',
  'Order Unit','MRP Type','MRP Controller','Lot Size','Min Lot Size','Max Lot Size','Fixed lot size',
  'MAXIMUM STOCK LEVEL','MRP Group','ABC Indicatore','Assembly Scrap','Roundong Value',
  'Roundong Point','Rounding Profile','Procurement Type','Special procurement type',
  'Storage Location','Planned Delivery Time in Days','In-house production time',
  'Scheduling Margin Key for Floats','Safety Stock','Backflash','Stock Determina Group',
  'Storage loc. for EP','Minimum Safty Stock','Coverage Profile','Planning Strategy Group',
  'Consumption mode','Consumption period: backward','Consumption period: forward',
  'Mixed MRP indicator','Total Rep. Leadtime','Repetitive manufacturing profile',
  'Dependent requirements ind','Indicator for Requirements Grouping',
  'MRP relevancy for dependent requirements','Scrap','Production Scheduling Profile',
  'Production Supervisor','Unit of Issue','Prod Unit','Underdelivery Tol','Overdelivery Tol',
  'Batch Entry','Phys Inventory Indicator','CC Fixed','Shelf Life','Total Shelf Life',
  'Negative Stock','Serial No Profile','Profit Center',
  'Material Authorization Group for Activities in QM','Stock Type','Catalog Profile',
  'Plant Status','QM in Procurement Is Active','QA Control Key','Inspection setup',
  'Certificate Type','Inspection Interval','Valuation Class','Valuation Class for Sales Order Stock',
  'Valuation Class for Project Stock','Material Ledger Act','Valuation Category','LIFO/FIFO-Relevant',
  'Pool number for LIFO valuation','Material-related origin','Origin Group','Costing Overhead Group',
  'Alternative BOM','Key for Task List Group','Group Counter','Qty Structure',
  'Price control indicator','moving average price','Standard price','Inspection Type',
];

const toSapDate = v => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return v;
  const dd = String(d.getUTCDate()).padStart(2,'0');
  const mm = String(d.getUTCMonth()+1).padStart(2,'0');
  return `${dd}.${mm}.${d.getUTCFullYear()}`;
};

const toSapRow = (r, lineNum) => [
  String(lineNum).padStart(2,'0'),
  r.material_number || '',
  r.industry_sector || '',
  r.material_type || '',
  r.plant || '',
  r.storage_location || '',
  r.valuation_type || '',
  r.sales_organization || '',
  r.material_stats_group || '',
  r.distribution_channel || '',
  // view checkboxes: Basic Data 1, Basic Data 2, Classification, Sales view 1, Sales View 2,
  // Sales/Plant data, View1, Purchasing, Storage View 1, Storage View 2,
  // Accounting View 1, Accounting View 2, MRP View 1, MRP View 2, MRP View 3,
  // MRP View 4, Quality View, Costing View 1, Costing View 2  → 19 columns
  'X','X','0','','','X','X','X','X','X','','','X','X','X','X','','X','X',
  r.material_description || '',
  r.base_uom || '',
  r.material_group || '',
  r.old_material_number || '',
  r.division || '',
  r.cross_plant_material_status || '',
  toSapDate(r.cross_plant_valid_from),
  r.gross_weight || '',
  r.net_weight || '',
  r.weight_unit || '',
  r.weight_unit_iso || '',
  r.volume || '',
  r.volume_unit || '',
  r.size_dimensions || '',
  r.packaging_material_group || '',
  r.basic_material || '',
  r.industry_desc || '',
  r.class_type || '',
  r.class || '',
  r.sales_unit || '',
  r.denominator || '',
  r.numerator || '',
  r.cross_dist_chain_status || '',
  r.dist_chain_mat_status || '',
  toSapDate(r.dist_chain_valid_from),
  r.tax_classification || '',
  r.delivering_plant || '',
  r.gen_item_category_group || '',
  r.item_category_group || '',
  r.material_pricing_group || '',
  r.acct_assignment_group || '',
  r.availability_check || '',
  r.transport_group || '',
  r.loading_group || '',
  r.packaging_material_type || '',
  r.allowed_packaging_weight || '',
  r.allowed_packaging_weight_unit || '',
  r.allowed_packaging_volume || '',
  r.allowed_packaging_volume_unit || '',
  r.material_group_1 || '',
  r.material_group_2 || '',
  r.material_group_3 || '',
  r.material_group_4 || '',
  r.material_group_5 || '',
  r.material_statistics_group || '',
  r.commodity_code || '',
  r.export_import_group || '',
  r.control_code || '',
  r.country_of_origin || '',
  r.region_of_origin || '',
  r.purchasing_value_key || '',
  r.purchasing_group || '',
  r.batch_management || '',
  r.gr_processing_time || '',
  r.quota_arrangement_usage || '',
  r.order_unit || '',
  r.mrp_type || '',
  r.mrp_controller || '',
  r.lot_size || '',
  r.min_lot_size || '',
  r.max_lot_size || '',
  r.fixed_lot_size || '',
  r.maximum_stock_level || '',
  r.mrp_group || '',
  r.abc_indicator || '',
  r.assembly_scrap || '',
  r.rounding_value || '',
  r.rounding_point || '',
  r.rounding_profile || '',
  r.procurement_type || '',
  r.special_procurement_type || '',
  r.mrp_storage_location || '',
  r.planned_delivery_time || '',
  r.in_house_production_time || '',
  r.scheduling_margin_key || '',
  r.safety_stock || '',
  r.backflush || '',
  r.stock_determination_group || '',
  r.storage_location_ep || '',
  r.min_safety_stock || '',
  r.coverage_profile || '',
  r.planning_strategy_group || '',
  r.consumption_mode || '',
  r.consumption_period_backward || '',
  r.consumption_period_forward || '',
  r.mixed_mrp_indicator || '',
  r.total_repl_leadtime || '',
  r.repetitive_mfg_profile || '',
  r.dependent_req_indicator || '',
  r.req_grouping_indicator || '',
  r.mrp_relevancy_dependent || '',
  r.scrap || '',
  r.production_scheduling_profile || '',
  r.production_supervisor || '',
  r.unit_of_issue || '',
  r.prod_unit || '',
  r.underdelivery_tolerance || '',
  r.overdelivery_tolerance || '',
  r.batch_entry || '',
  r.phys_inventory_indicator || '',
  r.cc_fixed || '',
  r.shelf_life || '',
  r.total_shelf_life || '',
  r.negative_stock || '',
  r.serial_number_profile || '',
  r.profit_center || '',
  r.qm_authorization_group || '',
  r.stock_type || '',
  r.catalog_profile || '',
  r.plant_status || '',
  r.qm_in_procurement_active || '',
  r.qa_control_key || '',
  r.inspection_setup || '',
  r.certificate_type || '',
  r.inspection_interval || '',
  r.valuation_class || '',
  r.valuation_class_so_stock || '',
  r.valuation_class_prj_stock || '',
  r.material_ledger_active || '',
  r.valuation_category || '',
  r.lifo_fifo_relevant || '',
  r.lifo_pool_number || '',
  r.material_related_origin || '',
  r.origin_group || '',
  r.costing_overhead_group || '',
  r.alternative_bom || '',
  r.task_list_group_key || '',
  r.group_counter_qty_structure || '',
  r.group_counter_qty_structure || '', // Qty Structure (same field)
  r.price_control_indicator || '',
  r.moving_average_price || '',
  r.standard_price || '',
  r.inspection_type || '',
];

const VENDOR_HEADERS = [
  'Sl No','Business Partner Number (System Generated)','Company code','Business Partner Grouping',
  'Title','Name 1','Name 2','Search Term 1','Search Term 2','Street','House Number','Street 1',
  'Street 2','Street3','City Postal Code','City1','city2-DISTRICT','Country Key',
  'Region (State, Province, County)','Language Key','Mobile_Number_01','Mobile_Number_02',
  'E-Mail (1) Address','Bank ID','Country/Region Key of Bank','Bank Key (IFSC)',
  'Bank account number','A/C Holder Name','Reconciliation Account in General Ledger',
  'Planning Group','Terms of Payment Key','Check Flag for Double Invoices or Credit Memos',
  'List of Respected Payment Methods','Account number of the alternative payee',
  'Withholding Tax Type','Withholding Tax Number','Withholding Tax code type',
  'Exe. Certificate No.','Exemption Rate','Date on Which Exemption Begins',
  'Date on Which Exemption Ends','Purchase Organisation','Order currency','Payt terms',
  'Incoterms','Incoterms 2','GR-Based IV check','Service-Based IV check','Supplier Schema',
  'PAN number','GST Tax type','GSTN Number','Customer','Plant','Shipping Condition',
];

const toVendorRow = (r, lineNum) => [
  lineNum,
  r.business_partner_number || '',
  r.company_code || '',
  r.bp_grouping || '',
  r.bp_title || '',
  r.name_1 || '',
  r.name_2 || '',
  r.search_term_1 || '',
  r.search_term_2 || '',
  r.street || '',
  r.house_number || '',
  r.street_1 || '',
  r.street_2 || '',
  r.street_3 || '',
  r.city_postal_code || '',
  r.city_1 || '',
  r.city_2_district || '',
  r.country_key || '',
  r.region || '',
  r.language_key || '',
  r.mobile_number_01 || '',
  r.mobile_number_02 || '',
  r.email_address || '',
  r.bank_id || '',
  r.bank_country_region_key || '',
  r.bank_key_ifsc || '',
  r.bank_account_number || '',
  r.account_holder_name || '',
  r.reconciliation_account || '',
  r.planning_group || '',
  r.terms_of_payment_key || '',
  r.double_invoice_check || '',
  r.payment_methods || '',
  r.alternative_payee_acct || '',
  r.withholding_tax_type || '',
  r.withholding_tax_number || '',
  r.withholding_tax_code_type || '',
  r.exemption_certificate_no || '',
  r.exemption_rate || '',
  r.exemption_start_date || '',
  r.exemption_end_date || '',
  r.purchase_organisation || '',
  r.order_currency || '',
  r.payment_terms || '',
  r.incoterms || '',
  r.incoterms_2 || '',
  r.gr_based_iv_check || '',
  r.service_based_iv_check || '',
  r.kalsk_schema_supplier || '',
  r.pan_number || '',
  r.gst_tax_type || '',
  r.gstn_number || '',
  '', // Customer — not in schema
  '', // Plant — not in schema
  '', // Shipping Condition — not in schema
];

const exportSapExcel = (rows, filename, headers, rowMapper) => {
  if (!rows.length) { toast.info('No requests to export'); return; }
  import('xlsx').then(XLSX => {
    const wb = XLSX.utils.book_new();

    // Build data array: header row + data rows
    const data = [
      headers,
      ...rows.map((r, i) => rowMapper(r, i + 1)),
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Column widths — 20 chars each
    ws['!cols'] = headers.map(() => ({ wch: 22 }));

    // Freeze top row
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };

    // Style header row cells bold + background
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[addr]) continue;
      ws[addr].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '0D1A6E' } },
        alignment: { horizontal: 'center', wrapText: true },
        border: {
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right:  { style: 'thin', color: { rgb: 'CCCCCC' } },
        },
      };
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, filename, { bookType: 'xlsx', type: 'binary', cellStyles: true });
  });
};

const IncomingRequests = ({ source }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [modalReq, setModalReq] = useState(null);
  const [form, setForm] = useState({ status: 'in_progress', it_remarks: '', generated_code: '' });
  const [infoMessage, setInfoMessage] = useState('');
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showData, setShowData] = useState(false);

  const fetchRequests = () => api.get('/requests/all').then(r => {
    const all = r.data;
    setRequests(source ? all.filter(x => x.source === source) : all);
  });
  useEffect(() => { fetchRequests(); }, [source]);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const actionableCount = requests.filter(r => PROCESSABLE.includes(r.status)).length;

  const handleExport = () => {
    const exportable = requests.filter(r => ['approved', 'in_progress'].includes(r.status));
    if (!exportable.length) { toast.info('No approved or in-progress requests to export'); return; }
    if (source === 'material_requests') {
      const endRow = Array(SAP_HEADERS.length).fill('END');
      exportSapExcel(
        [...exportable, null],
        `SAP_Material_Requests_${new Date().toISOString().slice(0,10)}.xlsx`,
        SAP_HEADERS,
        (r, i) => r === null ? endRow : toSapRow(r, i)
      );
    } else {
      exportSapExcel(
        exportable,
        `SAP_Vendor_Requests_${new Date().toISOString().slice(0,10)}.xlsx`,
        VENDOR_HEADERS,
        toVendorRow
      );
    }
  };

  const code = selected ? CODE_FIELD[selected.source] : null;
  const existingCode = code ? String(selected[code.column] ?? '').trim() : '';
  const codeProvided = String(form.generated_code || '').trim() || existingCode;
  const needsCode = Boolean(code) && !codeProvided;
  const canProcess = Boolean(selected) && PROCESSABLE.includes(selected.status);

  const selectRow = r => {
    setSelected(r);
    const cf = CODE_FIELD[r.source];
    setForm({ status: 'in_progress', it_remarks: r.it_remarks || '', generated_code: cf ? String(r[cf.column] ?? '') : '' });
    setShowData(false);
    setShowInfoBox(false);
    setInfoMessage('');
  };

  const onCommented = comment => setSelected(prev => ({ ...prev, comments: [...(prev.comments || []), comment] }));

  const handleRequestInfo = async () => {
    if (!infoMessage.trim()) return;
    setLoading(true);
    try {
      await api.patch(`/requests/${selected.id}/request-info`, { source: selected.source, message: infoMessage.trim() });
      toast.success('Request sent back to user for more information');
      setSelected(null);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to send request'); }
    finally { setLoading(false); }
  };

  const handleProcess = async () => {
    if (!selected || !canProcess) return;
    if (form.status === 'completed' && needsCode) { toast.error(`${code.label} is required to complete this request`); return; }
    setLoading(true);
    try {
      await api.patch(`/requests/${selected.id}/process`, { ...form, source: selected.source });
      toast.success(form.status === 'completed' ? 'Master created — request closed' : 'Marked in progress');
      setSelected(null); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setLoading(false); }
  };

  const approvals = selected?.approvals || [];
  const approvedCount = approvals.filter(a => a.decision === 'approved').length;

  return (
    <div>
      <div style={S.banner}>
        <div style={S.bannerBg} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={S.bannerTitle}>
            {source === 'material_requests' ? 'Material Requests' : source === 'vendor_requests' ? 'Vendor Requests' : 'Incoming Requests'}
          </div>
          <div style={S.bannerSub}>Click a row to process approved work</div>
        </div>
        <div style={S.blob} />
      </div>

      <div style={S.layout}>
        {/* ── Table ── */}
        <div style={S.tableCard}>
          <div style={S.tableTop}>
            <div style={S.cardTitle}>
              {source === 'material_requests' ? 'Material Requests' : source === 'vendor_requests' ? 'Vendor Requests' : 'Incoming Requests'}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <StatusFilter value={filter} onChange={setFilter} requests={requests} />
              <div style={S.countBadge}>{actionableCount} to process</div>
              <button style={S.exportBtn} onClick={handleExport} title="Download approved requests as SAP Excel">
                ⬇ Export Excel
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr style={S.thead}>
                  {['Request #', 'Title', 'Category', 'Requested By', 'Approved By', 'Status', 'Date'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={7} style={S.empty}>No requests</td></tr>}
                {filtered.map((r, i) => {
                  const active = selected?.source === r.source && selected?.id === r.id;
                  return (
                    <tr key={`${r.source}-${r.id}`}
                      style={{ ...S.tr, background: active ? '#fff8f0' : i % 2 === 0 ? '#fff' : '#f8faff', cursor: 'pointer' }}
                      onClick={() => selectRow(r)}>
                      <td style={{ ...S.td, fontWeight: 700, color: '#4f6ef7' }}>
                        <span onClick={e => { e.stopPropagation(); setModalReq(r); }} style={S.reqLink}>{r.request_number}</span>
                      </td>
                      <td style={{ ...S.td, fontWeight: 600, color: '#0B1554' }}>{r.title}</td>
                      <td style={S.td}>{r.category}</td>
                      <td style={S.td}>{r.requester_name}</td>
                      <td style={S.td}>{r.reviewer_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td style={S.td}><StatusBadge status={r.status} /></td>
                      <td style={{ ...S.td, color: '#94a3b8' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Side Panel ── */}
        {selected && (
          <div style={S.panel}>
            <div style={S.panelHead}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.panelReqNum}>{selected.request_number}</div>
                <div style={S.panelMeta}>{selected.category} · {selected.requester_name}</div>
              </div>
              <button onClick={() => setSelected(null)} style={S.closeBtn}>✕</button>
            </div>

            <div style={S.panelScroll}>
              {/* Summary strip */}
              <div style={S.strip}>
                <div style={S.stripItem}>
                  <span style={S.stripLabel}>Status</span>
                  <StatusBadge status={selected.status} />
                </div>
                <div style={S.stripItem}>
                  <span style={S.stripLabel}>Date</span>
                  <span style={S.stripVal}>{new Date(selected.created_at).toLocaleDateString()}</span>
                </div>
                <div style={S.stripItem}>
                  <span style={S.stripLabel}>Approvals</span>
                   <span style={{ ...S.stripVal, color: '#4f6ef7', fontWeight: 800 }}>{approvedCount}/{approvals.length}</span>

                </div>
              </div>

              {/* Title + data toggle */}
              <div style={S.section}>
                <div style={S.sectionRow}>
                  <div style={S.sectionLabel}>Request</div>
                  <button style={S.toggleBtn} onClick={() => setShowData(v => !v)}>
                    {showData ? '▲ Hide data' : '▼ View data'}
                  </button>
                </div>
                <div style={S.bigTitle}>{selected.title}</div>
                {selected.description && <div style={S.desc}>{selected.description}</div>}
                {showData && (
                  <div style={S.dataWrap}>
                    <RequestDetail request={selected} />
                  </div>
                )}
              </div>

              {/* Approval chain — read-only for IT */}
              {approvals.length > 0 && (
                <div style={S.section}>
                  <div style={S.sectionLabel}>Approval History</div>
                  <div style={S.progressTrack}>
                    <div style={{ ...S.progressFill, width: `${(approvedCount / approvals.length) * 100}%` }} />
                  </div>
                  <div style={S.timeline}>
                    {approvals.map((a, i) => {
                      const ds = DECISION_STYLE[a.decision] || DECISION_STYLE.pending;
                      return (
                        <div key={i} style={S.tlRow}>
                          <div style={S.tlLeft}>
                            <div style={{ ...S.tlDot, background: ds.dot, boxShadow: `0 0 0 3px ${ds.bg}` }} />
                            {i < approvals.length - 1 && <div style={S.tlLine} />}
                          </div>
                          <div style={S.tlBody}>
                            <div style={S.tlName}>{a.approver_name}</div>
                            <div style={S.tlEmail}>{a.approver_email}</div>
                            <span style={{ ...S.tlBadge, background: ds.bg, color: ds.dot }}>{ds.label}</span>
                            {a.remarks && <div style={S.tlRemarks}>"{a.remarks}"</div>}
                            {a.decided_at && <div style={S.tlDate}>{new Date(a.decided_at).toLocaleString()}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Approver remarks */}
              {selected.approver_remarks && (
                <div style={S.section}>
                  <div style={S.sectionLabel}>Approver Remarks</div>
                  <div style={S.remarkBox}>{selected.approver_remarks}</div>
                </div>
              )}

              <RequestComments request={selected} canPost={user?.role === 'it_support' || user?.role === 'admin'} onPosted={onCommented} />

              {/* IT Action section */}
              <div style={{ ...S.section, background: canProcess ? '#fffdf5' : '#f8faff' }}>
                <div style={S.sectionLabel}>IT Action</div>

                {!canProcess ? (
                  <div style={S.lockNote}>
                    {selected.status === 'completed'
                      ? `Completed${selected.material_number ? ` — Material # ${selected.material_number}` : selected.business_partner_number ? ` — BP # ${selected.business_partner_number}` : ''}`
                      : `Read only — status is ${selected.status.replace('_', ' ')}`}
                  </div>
                ) : (
                  <>
                    {/* Request More Info toggle */}
                    {!showInfoBox ? (
                      <button style={S.btnInfo} onClick={() => setShowInfoBox(true)}>
                        ↩ Request More Info from User
                      </button>
                    ) : (
                      <div style={S.infoBox}>
                        <div style={S.infoBoxLabel}>What additional information do you need?</div>
                        <textarea
                          style={S.textarea}
                          value={infoMessage}
                          onChange={e => setInfoMessage(e.target.value)}
                          placeholder="Describe what the user needs to provide or correct…"
                          autoFocus
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            style={{ ...S.btn, background: 'linear-gradient(135deg,#0891b2,#06b6d4)', opacity: loading || !infoMessage.trim() ? 0.5 : 1 }}
                            onClick={handleRequestInfo}
                            disabled={loading || !infoMessage.trim()}>
                            {loading ? 'Sending…' : '✓ Send to User'}
                          </button>
                          <button style={S.btnCancel} onClick={() => { setShowInfoBox(false); setInfoMessage(''); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {code && (
                      <div style={S.codeBox}>
                        <label style={S.codeLabel}>
                          {code.label}
                          {existingCode
                            ? <span style={S.codeOk}> · provided by user</span>
                            : <span style={S.codeNeed}> · required from SAP</span>}
                        </label>
                        <input
                          style={{ ...S.input, ...(needsCode && form.status === 'completed' ? S.inputError : null) }}
                          value={form.generated_code}
                          onChange={e => setForm({ ...form, generated_code: e.target.value })}
                          placeholder="e.g. 0000012345"
                        />
                        {existingCode && !form.generated_code && (
                          <div style={S.codeHint}>Leave blank to keep existing value: <strong>{existingCode}</strong></div>
                        )}
                      </div>
                    )}

                    <label style={S.fieldLabel}>Update Status</label>
                    <select style={S.select} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed — Master Created</option>
                    </select>

                    <label style={S.fieldLabel}>IT Remarks</label>
                    <textarea style={S.textarea} value={form.it_remarks}
                      onChange={e => setForm({ ...form, it_remarks: e.target.value })}
                      placeholder="Add IT remarks…" />

                    <button
                      style={{ ...S.btn, opacity: loading || (form.status === 'completed' && needsCode) ? 0.5 : 1 }}
                      onClick={handleProcess}
                      disabled={loading || (form.status === 'completed' && needsCode)}>
                      {loading ? 'Updating…' : form.status === 'completed' ? `✓ Complete${code ? ` & Save ${code.label}` : ''}` : '↻ Save as In Progress'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {modalReq && <RequestFormModal request={modalReq} onClose={() => setModalReq(null)} />}
    </div>
  );
};

const S = {
  banner: { background: 'linear-gradient(135deg,#060d3a 0%,#1a0a00 50%,#3a1a00 100%)', borderRadius: 18, marginBottom: 24, padding: '32px 36px', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(6,13,58,0.25)' },
  bannerBg: { position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 80% 50%,rgba(234,88,12,0.2) 0%,transparent 60%)' },
  bannerTitle: { color: '#fff', fontSize: 24, fontWeight: 800, marginBottom: 8, letterSpacing: -0.3 },
  bannerSub: { color: 'rgba(255,255,255,0.6)', fontSize: 14 },
  blob: { position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(234,88,12,0.12)', top: -80, right: 60, filter: 'blur(30px)' },
  layout: { display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' },
  tableCard: { flex: 2, background: '#fff', borderRadius: 18, boxShadow: '0 4px 20px rgba(11,21,84,0.06)', overflow: 'hidden', minWidth: 400, border: '1px solid rgba(79,110,247,0.06)' },
  tableTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '16px 18px', borderBottom: '1px solid #f1f5f9' },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#0B1554' },
  catTabs: { display: 'flex', gap: 4 },
  catTab: { padding: '6px 16px', borderRadius: 20, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  catTabActive: { background: '#0B1554', color: '#fff', borderColor: '#0B1554' },
   exportBtn: { padding: '6px 14px', borderRadius: 8, border: '1.5px solid #4f6ef7', background: '#eef2ff', color: '#1A2C80', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },

  countBadge: { background: '#fff7ed', color: '#c2410c', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid #fed7aa', whiteSpace: 'nowrap' },
  reqLink: { cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', color: '#4f6ef7' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)' },
  th: { padding: '13px 18px', color: '#F7D506', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '13px 18px', fontSize: 13.5, color: '#374151' },
  empty: { textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 14 },

  panel: { flex: 1, background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(11,21,84,0.12)', minWidth: 380, maxWidth: 460, overflow: 'hidden', border: '1px solid rgba(79,110,247,0.06)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' },
  panelHead: { background: 'linear-gradient(135deg,#060d3a,#1a0a00)', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 },
  panelReqNum: { color: '#F7D506', fontWeight: 800, fontSize: 14, marginBottom: 2 },
  panelMeta: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  closeBtn: { background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
  panelScroll: { overflowY: 'auto', flex: 1 },

  strip: { display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '12px 20px' },
  stripItem: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '0 8px', borderRight: '1px solid #f1f5f9' },
  stripLabel: { fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.7, textTransform: 'uppercase' },
  stripVal: { fontSize: 13, fontWeight: 700, color: '#0B1554' },

  section: { padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  sectionRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  sectionLabel: { fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase' },
  toggleBtn: { fontSize: 11, fontWeight: 700, color: '#ea580c', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' },
  bigTitle: { fontSize: 14, fontWeight: 700, color: '#0B1554', marginBottom: 4, lineHeight: 1.4 },
  desc: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginBottom: 8 },
  dataWrap: { marginTop: 12, borderTop: '1px solid #f0f2f8', paddingTop: 12 },

  progressTrack: { height: 5, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden', marginBottom: 14 },
   progressFill: { height: '100%', borderRadius: 3, background: '#4f6ef7', transition: 'width 0.4s' },


  timeline: { display: 'flex', flexDirection: 'column' },
  tlRow: { display: 'flex', gap: 12, marginBottom: 4 },
  tlLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 },
  tlDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0, marginTop: 3 },
  tlLine: { width: 2, flex: 1, background: '#e2e8f0', margin: '4px 0' },
  tlBody: { flex: 1, paddingBottom: 12 },
  tlName: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  tlEmail: { fontSize: 11.5, color: '#64748b', marginBottom: 4 },
  tlBadge: { display: 'inline-block', fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 20, letterSpacing: 0.3 },
  tlRemarks: { fontSize: 12, color: '#475569', fontStyle: 'italic', marginTop: 5, lineHeight: 1.5 },
  tlDate: { fontSize: 10.5, color: '#94a3b8', marginTop: 3 },

  remarkBox: { fontSize: 13, color: '#374151', background: '#f8faff', border: '1px solid #e5e9f5', borderRadius: 10, padding: '10px 12px', lineHeight: 1.6, whiteSpace: 'pre-wrap' },

  codeBox: { background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '10px 12px', marginBottom: 12 },
  codeLabel: { display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 },
  codeOk: { fontSize: 11, fontWeight: 700, color: '#15803d' },
  codeNeed: { fontSize: 11, fontWeight: 700, color: '#c2410c' },
  codeHint: { fontSize: 11, color: '#9a3412', marginTop: 6, lineHeight: 1.4 },

  fieldLabel: { display: 'block', fontWeight: 700, fontSize: 12, marginBottom: 6, color: '#374151', marginTop: 12 },
  select: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13.5, boxSizing: 'border-box', color: '#0B1554', background: '#f9fafb' },
  input: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13.5, boxSizing: 'border-box', color: '#0B1554', background: '#f9fafb' },
  inputError: { borderColor: '#dc2626', background: '#fef2f2' },
  textarea: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13.5, height: 80, resize: 'vertical', boxSizing: 'border-box', marginTop: 4 },
  btn: { width: '100%', marginTop: 14, background: 'linear-gradient(135deg,#ea580c,#f97316)', color: '#fff', border: 'none', padding: '12px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, boxShadow: '0 4px 14px rgba(234,88,12,0.35)' },
  btnInfo: { width: '100%', marginBottom: 12, background: 'linear-gradient(135deg,#0891b2,#06b6d4)', color: '#fff', border: 'none', padding: '10px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  btnCancel: { flex: 1, padding: '10px', background: '#f1f5f9', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  infoBox: { background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 10, padding: '12px', marginBottom: 12 },
  infoBoxLabel: { fontSize: 12, fontWeight: 700, color: '#0e7490', marginBottom: 8 },
  lockNote: { fontSize: 12.5, fontWeight: 600, color: '#374151', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' },
};

export default IncomingRequests;
